import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import clsx from "clsx";
import { useOverlayFlag } from "../../lib/overlayFlag";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
}

/** Accessible modal: focus-trapped-ish, Escape to close, labelled by its title (Section 50). */
export const Modal: React.FC<ModalProps> = ({ open, onClose, title, description, children, footer, size = "md" }) => {
  const ref = useRef<HTMLDivElement>(null);
  useOverlayFlag(open);
  // Mounts instantly at open (so Escape/focus wiring is ready) but starts the
  // enter transition one frame later — without this the panel would jump
  // straight to its final state instead of animating in.
  const [entered, setEntered] = useState(false);

  // Callers almost always pass onClose as a fresh inline arrow function, so
  // it's a new reference on every render — kept out of the effect's deps via
  // a ref rather than depending on it directly, otherwise a parent re-render
  // triggered by typing into a field inside this modal would re-run the
  // effect below and re-focus the dialog container on every keystroke,
  // stealing focus straight back out of whatever the user just clicked into.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCloseRef.current();
    }
    document.addEventListener("keydown", onKey);
    ref.current?.focus();
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => {
      document.removeEventListener("keydown", onKey);
      cancelAnimationFrame(raf);
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className={clsx(
          "absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300 ease-out",
          entered ? "opacity-100" : "opacity-0"
        )}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className={clsx(
          "relative z-10 max-h-[85vh] w-full overflow-y-auto rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] focus:outline-none",
          entered ? "translate-y-0 scale-100 opacity-100" : "translate-y-3 scale-90 opacity-0",
          size === "sm" && "max-w-sm",
          size === "md" && "max-w-lg",
          size === "lg" && "max-w-2xl",
          size === "xl" && "max-w-4xl"
        )}
      >
        <div className="flex items-start justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 id="modal-title" className="text-base font-semibold text-slate-900">
              {title}
            </h2>
            {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
          </div>
          <button
            onClick={onClose}
            aria-label="Close dialog"
            className="rounded-md p-1 text-slate-400 transition-all duration-150 hover:rotate-90 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};
